/**
 * Tags Schema Definitions
 *
 * Spec-first schema definitions for tag management system.
 * These schemas drive oRPC type generation and validation.
 */

import { z } from "zod";
import {
  UuidField,
  ParticipantIdField,
  TimestampField,
  MetadataField,
  ErrorResponse,
} from "./common";

export const TagType = z.enum([
  "participant",
  "group",
  "general",
  "uri",
  "file",
  "media",
  "chat-message",
  "workspace",
  "thread",
  "conversation",
]);

// Core Tag entity
const Tag = z.object({
  id: z.string(),
  name: z.string().min(1).max(100),
  type: TagType,
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  icon: z.string().max(250).optional(),
  description: z.string().max(500).optional(),
  metadata: MetadataField,
  workspaceId: UuidField,
  namespace: z.string().min(1).max(50).default("general"), // Namespace for tag organization
  createdAt: TimestampField,
  updatedAt: TimestampField,
});

// Response schemas
const TagListResponse = z.object({
  tags: z.array(Tag),
});

// CRUD operation schemas
const CreateTagRequest = z.object({
  name: z.string().min(1).max(100),
  type: TagType,
  color: z
    .string()
    .regex(/^#[0-9a-fA-F]{6}$/)
    .optional(),
  icon: z.string().max(250).optional(),
  description: z.string().max(500).optional(),
  metadata: MetadataField,
  workspaceId: UuidField,
  namespace: z.string().min(1).max(50).default("general"), // Namespace for tag organization
});

const UpdateTagRequest = z
  .object({
    name: z.string().min(1).max(100),
    type: TagType,
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    icon: z.string().max(250),
    description: z.string().max(500),
    metadata: z.object({}).partial(),
    namespace: z.string().min(1).max(50),
  })
  .partial();

const DeleteTagRequest = z.object({
  tagId: z.string(),
  workspaceId: UuidField,
  namespace: z.string().min(1).max(50).optional(),
});

const SearchTagsRequest = z.object({
  workspaceId: UuidField,
  query: z.string().min(1),
  matchFields: z.string().default("name"), // CSV: "name", "description", "name,description"
  caseSensitive: z.boolean().default(false),
  namespace: z.string().min(1).max(50).optional(),
  type: TagType.optional(),
  limit: z.number().int().min(1).max(100).default(10),
  offset: z.number().int().min(0).default(0),
});

const SearchTagsResponse = z.object({
  tags: z.array(Tag),
  total: z.number().int().min(0),
  limit: z.number().int(),
  offset: z.number().int(),
  hasMore: z.boolean(),
});

const TagResponse = z.object({
  success: z.literal(true),
  data: Tag,
});

const DeleteTagResponse = z.object({
  success: z.literal(true),
  message: z.string(),
});

// Tag Assignment entity
const TagAssignment = z.object({
  tagId: z.string(),
  entityType: TagType,
  entityId: z.string(),
  workspaceId: UuidField,
  assignedAt: TimestampField,
  assignedBy: ParticipantIdField, // participantId who assigned the tag
  metadata: MetadataField,
});

// Tag Events for real-time updates
const TagEvent = z.object({
  id: z.string(),
  type: z.literal("tags-update"),
  workspaceId: UuidField,
  timestamp: z.string(),
  data: z.object({
    tags: z.record(z.string(), z.array(Tag)), // namespace -> tags mapping
  }),
});

const TagCreateEvent = z.object({
  action: z.literal("created"),
  tag: Tag,
  timestamp: TimestampField,
});

const TagUpdateEvent = z.object({
  action: z.literal("updated"),
  tag: Tag,
  timestamp: TimestampField,
});

const TagDeleteEvent = z.object({
  action: z.literal("deleted"),
  tagId: z.string(),
  workspaceId: UuidField,
  timestamp: TimestampField,
});

const TagAssignmentEvent = z.object({
  action: z.enum(["assigned", "unassigned"]),
  assignment: TagAssignment,
  timestamp: TimestampField,
});

// Export all schemas
export const schemas = {
  Tag,
  TagType,
  TagAssignment,
  TagListResponse,
  CreateTagRequest,
  UpdateTagRequest,
  DeleteTagRequest,
  SearchTagsRequest,
  SearchTagsResponse,
  TagResponse,
  DeleteTagResponse,
  TagEvent,
  TagCreateEvent,
  TagUpdateEvent,
  TagDeleteEvent,
  TagAssignmentEvent,
  ErrorResponse,
};

// oRPC Type Inference Support
// These types enable proper oRPC client generation and type inference
export type SchemaTypes = {
  [K in keyof typeof schemas]: z.infer<(typeof schemas)[K]>;
};

// Re-export for convenience
export type { SchemaTypes as TagsSchemaTypes };

export { schemas as tagsSchemas };
